'use client';

import { useEffect } from 'react';
import { safeUUID } from '@/lib/safe-uuid';

const COOKIE = 'ic_attribution';
const MAX_AGE = 90 * 24 * 60 * 60; // 90 giorni

/**
 * Cattura l'attribuzione FIRST-TOUCH al primo landing e la salva in un cookie
 * (90gg). Non sovrascrive se già presente. Alla conversione (booking) il server
 * legge il cookie e snapshotta utm_* sul record. Vedi lib/attribution.ts.
 */
export default function UtmCapture() {
  useEffect(() => {
    try {
      // Browser admin/owner (opt-out) → non tracciare.
      if (document.cookie.split('; ').some((c) => c.startsWith('ic_noanalytics=1'))) return;

      const p = new URLSearchParams(window.location.search);
      const get = (k: string) => (p.get(k) || '').trim().slice(0, 160) || undefined;

      let source = get('utm_source');
      let medium = get('utm_medium');
      const campaign = get('utm_campaign');
      const term = get('utm_term');
      const content = get('utm_content');
      const gclid = get('gclid');
      const fbclid = get('fbclid');

      // Referrer esterno (host)
      let referrer: string | undefined;
      try {
        if (document.referrer) {
          const r = new URL(document.referrer);
          if (r.hostname && r.hostname !== window.location.hostname) referrer = r.hostname.slice(0, 300);
        }
      } catch { /* ignore */ }

      // Deriva sorgente se manca utm_source esplicito.
      if (!source) {
        if (gclid) { source = 'google'; medium = medium || 'cpc'; }
        else if (fbclid) { source = 'facebook'; medium = medium || 'social'; }
        else if (referrer) { source = referrer; medium = medium || 'referral'; }
      }

      // ── Log VISITA (TUTTO il traffico, anche diretto) — 1× per sessione ──
      // Il server dedup via session_id UNIQUE; il flag sessionStorage evita
      // POST ripetute a ogni navigazione.
      try {
        if (!window.sessionStorage.getItem('ic-visit-sent')) {
          let sid = window.sessionStorage.getItem('ic-session-id');
          if (!sid) { sid = safeUUID(); window.sessionStorage.setItem('ic-session-id', sid); }
          window.sessionStorage.setItem('ic-visit-sent', '1');
          fetch('/api/track/visit', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            keepalive: true,
            body: JSON.stringify({
              session_id: sid,
              source, medium, campaign, content, term,
              referrer,
              landing_path: window.location.pathname.slice(0, 300),
            }),
          }).catch(() => { /* fire-and-forget */ });
        }
      } catch { /* ignore */ }

      // ── Cookie FIRST-TOUCH per attribuzione conversione (booking) ──
      // Solo se c'è un segnale e il cookie non esiste già.
      if (!source && !campaign) return;
      if (document.cookie.split('; ').some((c) => c.startsWith(COOKIE + '='))) return;
      const data = { source, medium, campaign, term, content, referrer, ts: Date.now() };
      const json = encodeURIComponent(JSON.stringify(data));
      document.cookie = `${COOKIE}=${json}; path=/; max-age=${MAX_AGE}; samesite=lax`;
    } catch { /* ignore */ }
  }, []);

  return null;
}
